import { atom } from 'jotai';
import { loadable } from 'jotai/utils';
import request from 'lib/request';
import type {
  ScheduledJobsResponse,
  TriggerJobResponse,
  TriggerJobErrorResponse,
} from './types';

// Refresh trigger atom - increment to refetch data
const refreshCounterAtom = atom(0);

// Base async atom that depends on refresh counter
const scheduledJobsBaseAtom = atom(async (get) => {
  get(refreshCounterAtom); // Creates dependency for refetch
  const response = await request.get<ScheduledJobsResponse>(
    '/admin/scheduled_jobs'
  );
  if ('error' in response) {
    throw new Error(response.error);
  }
  return response;
});

// Loadable wrapper for loading/error states
const scheduledJobsLoadableAtom = loadable(scheduledJobsBaseAtom);

// Combined atom that provides data and refetch capability
export const scheduledJobsAtom = atom(
  (get) => get(scheduledJobsLoadableAtom),
  (_get, set) => {
    set(refreshCounterAtom, (c) => c + 1);
  }
);

export const triggerJob = async (
  className: string
): Promise<TriggerJobResponse> => {
  const abortController = new AbortController();
  const response = await request.post<
    TriggerJobResponse,
    TriggerJobErrorResponse
  >('/admin/scheduled_jobs/trigger', { job_class: className }, abortController);

  if (response.type === 'error' && response.data?.error) {
    throw new Error(response.data.error);
  }

  if (response.type === 'fatal' || response.type === 'abort') {
    throw new Error(response.message);
  }

  if (response.type === 'success') {
    return response.data;
  }

  throw new Error('Unexpected response');
};
